import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from 'react-router-dom';
import {
  Clock,
  Calendar,
  Search,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  FileText,
  User,
  MapPin,
  Timer,
  Plus,
  Edit,
  Trash2,
  Building,
  X,
  Mail,
  Users,
  UserCheck,
  UserX,
  AlarmClock,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Upload
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { formatDate, formatTime } from "../../../utils/stringUtils";
import { toast } from 'react-hot-toast';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import WarningPopup from "../../../components/WarningPopup";
import BulkUploadModal from "../../../components/common/BulkUploadModal";

const parseTimeMinutes = (timeStr) => {
  if (!timeStr || timeStr === 'N/A') return 0;
  const parts = timeStr.split(':').map(Number);
  if (parts.length < 2) return 0;
  const minutes = parts[0] * 60 + parts[1];
  const seconds = parts.length > 2 ? parts[2] / 60 : 0;
  return minutes + seconds;
};

const formatDuration = (minutes) => {
  if (minutes <= 0) return "0m";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const calculateWorkingHours = (checkIn, checkOut) => {
  if (!checkIn || !checkOut || checkIn === 'N/A' || checkOut === 'N/A') return 'N/A';
  try {
    const startMins = parseTimeMinutes(checkIn);
    const endMins = parseTimeMinutes(checkOut);
    let diff = endMins - startMins;
    if (diff < 0) diff += 24 * 60;
    return formatDuration(diff);
  } catch (e) {
    return 'N/A';
  }
};

const ATTENDANCE_RULES = {
  LATE_THRESHOLD: 9 * 60 + 30, // 9:30 AM
  EARLY_OUT_THRESHOLD: 18 * 60 + 30, // 6:30 PM
};

const STATUS_LABEL_MAP = {
  present:        'Present',
  late:           'Late',
  missed:         'Missed',
  absent:         'Absent',
  half_day:       'Half Day',
  'half-day':     'Half Day',
  half_day_leave: 'Half Day Leave',
  short_hours:    'Short Hours',
  early_left:     'Early Left',
  yet_checkout:   'Yet to Check Out',
  incomplete:     'Incomplete',
  week_off:       'Week Off',
  holiday:        'Holiday',
  on_leave:       'On Leave',
  regularized:    'Regularized',
};

const getAttendanceStatusInfo = (checkIn, checkOut, directStatus) => {
  // If backend provided a direct status, use it to ensure sync
  if (directStatus && directStatus !== 'all') {
    const status = directStatus.toLowerCase();
    const label = STATUS_LABEL_MAP[status] || status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    let lateBy = 0;
    if (status === 'late') {
      const inMins = parseTimeMinutes(checkIn);
      lateBy = Math.max(0, inMins - ATTENDANCE_RULES.LATE_THRESHOLD);
    }

    return { status, label, lateBy };
  }

  const inMins = parseTimeMinutes(checkIn);
  const outMins = parseTimeMinutes(checkOut);

  if (!inMins && !outMins) return { status: 'absent', label: 'Absent' };
  if ((inMins && !outMins) || (!inMins && outMins)) return { status: 'missed', label: 'Missed' };

  if (inMins > ATTENDANCE_RULES.LATE_THRESHOLD) {
    const lateBy = inMins - ATTENDANCE_RULES.LATE_THRESHOLD;
    return { status: 'late', label: 'Late', lateBy };
  }

  return { status: 'present', label: 'Present' };
};

const getEarlyOutInfo = (checkOut) => {
  if (!checkOut || checkOut === 'N/A') return null;
  const outMins = parseTimeMinutes(checkOut);
  if (outMins > 0 && outMins < ATTENDANCE_RULES.EARLY_OUT_THRESHOLD) {
    return { isEarly: true, earlyBy: ATTENDANCE_RULES.EARLY_OUT_THRESHOLD - outMins };
  }
  return { isEarly: false, earlyBy: 0 };
};

const AttendanceLogs = ({ onBack, userRole }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "all");
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || "");
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || "");
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    employee: "",
    date: "",
    clockIn: "",
    clockOut: "",
    status: "present",
    remarks: "",
  });



  // State for searchable employee dropdown
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  // Store selected employee data to persist even after search is cleared
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
    setCurrentPage(1);
  };

  // Debounce search term to avoid excessive API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce employee search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay for employee search

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // React Query: Search Employees (fetch when dropdown is open, load initial if search empty)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['attendance-logs-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      const trimmedSearch = debouncedEmployeeSearch?.trim() || "";
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
                  }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          return data.data.employees || [];
        }
      }
      return [];
    },
    enabled: employeeSearchOpen, // Call when search dropdown is open
    staleTime: 0,
  });

  // React Query: Load Attendance Logs with filters and pagination
  const { data: attendanceLogsData, isLoading, refetch: refetchAttendanceLogs } = useQuery({
    queryKey: ['attendance-logs', currentPage, itemsPerPage, debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", itemsPerPage.toString());
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (filterStartDate) queryParams.append("startDate", filterStartDate);
      if (filterEndDate) queryParams.append("endDate", filterEndDate);
      // Changed from "name" to "search" for biometric API
      if (debouncedSearchTerm) queryParams.append("search", debouncedSearchTerm);

      // NEW: Biometric Attendance API (synced from MSSQL)
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
                  },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseCode === 200) {
        return data.data;
      } else {
        throw new Error(data.responseMessage || 'Failed to load attendance logs');
      }
    },
    staleTime: 0,
    refetchOnMount: true,
  });

  // React Query: Fetch Attendance Stats (KPI Cards)
  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['attendance-stats'],
    queryFn: async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/stats`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
                  },
      });
      if (response.ok) {
        const data = await response.json();
        return data.data;
      }
      return { totalEmployees: 0, presentToday: 0, lateToday: 0, absentToday: 0 };
    },
    staleTime: 0,
  });

  // React Query: Delete Attendance Log Mutation
  const deleteLogMutation = useMutation({
    mutationFn: async (logId) => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/${logId}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
                    'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete attendance log');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance-logs'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-stats'] });
    },
  });


  // Get employees to display in dropdown (only searched results, no initial load)
  const dropdownEmployees = searchedEmployeesData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  // Close dropdown when modal closes (but preserve selectedEmployeeData)
  useEffect(() => {
    if (!showCreateModal && !showEditModal) {
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      // Don't clear selectedEmployeeData here - it should persist until form is reset
    }
  }, [showCreateModal, showEditModal]);

  // When editing, fetch employee data if employee ID is set but employee data is missing
  useEffect(() => {
    const fetchEmployeeForEdit = async () => {
      if (showEditModal && formData.employee && !selectedEmployeeData) {
        try {
          const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(formData.employee)}`;
          const response = await authFetch(url, {
        credentials: 'include',
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
                          }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.responseCode === 200) {
              const employeesArr = data.data.employees || [];
              // Try to find exact match by _id
              const employee = employeesArr.find((emp) => emp._id === formData.employee || emp.employeeId === formData.employee);
              if (employee) {
                setSelectedEmployeeData(employee);
              }
            }
          }
        } catch (error) {
        }
      }
    };

    fetchEmployeeForEdit();
  }, [showEditModal, formData.employee, selectedEmployeeData]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-3 w-3 ml-1 text-blue-600" /> :
      <ChevronDown className="h-3 w-3 ml-1 text-blue-600" />;
  };

  // Extract logs and pagination from query data
  const attendanceLogsRaw = attendanceLogsData?.logs || [];

  // Process logs to ensure punch arrays are sorted and checkIn/checkOut are correct
  const processedLogs = useMemo(() => {
    return attendanceLogsRaw.map(log => {
      // Sort checkInPunches and checkOutPunches arrays
      const sortedCheckInPunches = log.checkInPunches
        ? [...log.checkInPunches].sort((a, b) => {
          // Sort time strings (HH:MM:SS format)
          return a.localeCompare(b);
        })
        : [];

      const sortedCheckOutPunches = log.checkOutPunches
        ? [...log.checkOutPunches].sort((a, b) => {
          // Sort time strings (HH:MM:SS format)
          return a.localeCompare(b);
        })
        : [];

      // Extract earliest check-in and latest check-out
      const earliestCheckIn = sortedCheckInPunches.length > 0
        ? sortedCheckInPunches[0]
        : log.checkIn;

      const latestCheckOut = sortedCheckOutPunches.length > 0
        ? sortedCheckOutPunches[sortedCheckOutPunches.length - 1]
        : log.checkOut;

      return {
        ...log,
        checkIn: earliestCheckIn,
        checkOut: latestCheckOut,
        checkInPunches: sortedCheckInPunches,
        checkOutPunches: sortedCheckOutPunches
      };
    });
  }, [attendanceLogsRaw]);

  const attendanceLogs = useMemo(() => {
    let sortableLogs = [...processedLogs];
    if (sortConfig.key !== null) {
      sortableLogs.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'employee':
            aValue = (a.employeeName || '').toLowerCase();
            bValue = (b.employeeName || '').toLowerCase();
            break;
          case 'date':
            aValue = a.date || '';
            bValue = b.date || '';
            break;
          case 'checkIn':
            aValue = a.checkIn || '00:00';
            bValue = b.checkIn || '00:00';
            break;
          case 'checkOut':
            aValue = a.checkOut || '00:00';
            bValue = b.checkOut || '00:00';
            break;
          case 'workingHours':
            const getMinutes = (log) => {
              if (!log.checkIn || !log.checkOut || log.checkIn === 'N/A' || log.checkOut === 'N/A') return -1;
              const start = parseTimeMinutes(log.checkIn);
              const end = parseTimeMinutes(log.checkOut);
              let diff = end - start;
              if (diff < 0) diff += 24 * 60;
              return diff;
            };
            aValue = getMinutes(a);
            bValue = getMinutes(b);
            break;
          case 'status':
            aValue = getAttendanceStatusInfo(a.checkIn, a.checkOut, a.status).status;
            bValue = getAttendanceStatusInfo(b.checkIn, b.checkOut, b.status).status;
            break;
          default:
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableLogs;
  }, [processedLogs, sortConfig]);
  const pagination = attendanceLogsData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: itemsPerPage
  };

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = debouncedSearchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== "all") f.status = filterStatus;
    if (filterStartDate) f.startDate = filterStartDate;
    if (filterEndDate) f.endDate = filterEndDate;
    return f;
  }, [debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate]);

  const currentPageLogIds = useMemo(
    () => attendanceLogs.map((log) => log.id).filter(Boolean),
    [attendanceLogs]
  );

  const exportSortBy = useMemo(() => {
    if (sortConfig.key === "employee") return "employeeName";
    if (sortConfig.key === "date") return "date";
    if (sortConfig.key === "checkIn") return "checkIn";
    if (sortConfig.key === "checkOut") return "checkOut";
    if (sortConfig.key === "workingHours") return "workingHours";
    if (sortConfig.key === "status") return "status";
    return "date";
  }, [sortConfig.key]);

  const exportSortOrder = useMemo(() => {
    if (sortConfig.key === null) return "desc";
    return sortConfig.direction;
  }, [sortConfig.key, sortConfig.direction]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      currentPageIds,
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const payload = {
        module: "attendance-logs",
        format,
        scope,
        filters: overrideFilters ?? baseExportFilters,
        page: overridePage ?? currentPage,
        limit: overrideLimit ?? itemsPerPage,
        sortBy: exportSortBy,
        sortOrder: exportSortOrder,
        selectedIds,
      };
      if (Array.isArray(currentPageIds) && currentPageIds.length > 0) {
        payload.currentPageIds = currentPageIds;
      }
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: "POST",
          headers: {
            "Content-Type": "application/json",
                      },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Export request failed");
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === "xlsx" ? "xlsx" : format;
      const fallbackName = `attendance-logs-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      exportSortBy,
      exportSortOrder,
    ]
  );

  const handleAttendanceLogsSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error("No selected logs to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} log(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleAttendanceLogsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === "currentPage" && !attendanceLogs.length) {
          toast.error("No logs to export on the current page.");
          return;
        }

        const totalFiltered = Number(pagination.totalLogs ?? 0);
        if (scope === "filtered" && totalFiltered === 0) {
          toast.error("No logs match the current filters.");
          return;
        }

        if (scope === "dateRange") {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error("Please select start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === "all") {
          const trimmed = debouncedSearchTerm?.trim();
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {
              ...(trimmed ? { search: trimmed } : {}),
              ...(filterStatus !== "all" ? { status: filterStatus } : {}),
            },
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
            currentPageIds: currentPageLogIds,
          });
        } else {
          return;
        }

        toast.success("Export downloaded.");
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || "Export failed. Please try again.");
      } finally {
        setExportLoading(false);
      }
    },
    [
      attendanceLogs.length,
      pagination.totalLogs,
      baseExportFilters,
      debouncedSearchTerm,
      filterStatus,
      requestBackendExport,
      currentPageLogIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate, itemsPerPage]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));

      return params;
    }, { replace: true });
  }, [debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate, currentPage, itemsPerPage, setSearchParams]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':      return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'late':         return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'missed':
      case 'yet_checkout':
      case 'incomplete':   return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'absent':       return <XCircle className="h-4 w-4 text-red-500" />;
      case 'half_day':
      case 'half-day':
      case 'half_day_leave':
      case 'short_hours':
      case 'early_left':   return <AlertCircle className="h-4 w-4 text-orange-400" />;
      case 'week_off':     return <Clock className="h-4 w-4 text-blue-400" />;
      case 'holiday':      return <Clock className="h-4 w-4 text-indigo-500" />;
      case 'on_leave':     return <Clock className="h-4 w-4 text-purple-500" />;
      case 'regularized':  return <CheckCircle className="h-4 w-4 text-teal-500" />;
      default:             return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':        return 'bg-green-100 text-green-800';
      case 'late':           return 'bg-yellow-100 text-yellow-800';
      case 'missed':
      case 'yet_checkout':
      case 'incomplete':     return 'bg-orange-100 text-orange-800';
      case 'absent':         return 'bg-red-100 text-red-800';
      case 'half_day':
      case 'half-day':       return 'bg-amber-100 text-amber-800';
      case 'half_day_leave': return 'bg-amber-100 text-amber-700';
      case 'short_hours':
      case 'early_left':     return 'bg-orange-100 text-orange-700';
      case 'week_off':       return 'bg-blue-100 text-blue-700';
      case 'holiday':        return 'bg-indigo-100 text-indigo-800';
      case 'on_leave':       return 'bg-purple-100 text-purple-800';
      case 'regularized':    return 'bg-teal-100 text-teal-800';
      default:               return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewDetails = async (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };


  if (showDetails && selectedLog) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(false)}
              className="hover:bg-gray-100 rounded-full p-2 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Back to attendance logs"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-xl font-bold text-gray-800 tracking-tight">
              Attendance Details
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
            <Calendar className="h-4 w-4" />
            <span className="font-medium">
              {selectedLog.date ? formatDate(selectedLog.date, { includeTime: false }) : 'N/A'}
            </span>
          </div>
        </div>

        <div className="p-6 bg-gray-50/50 space-y-6">

          {/* Section 1: Employee Details (Row Wise) */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
              Employee Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <span className="text-xs text-gray-400 block mb-1">Name</span>
                <div className="font-bold text-gray-900 capitalize text-lg break-words">
                  {selectedLog.employeeName || 'Unknown'}
                </div>
                <div className="text-xs text-gray-500 capitalize">{selectedLog.designation || 'Employee'}</div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block mb-1">Department</span>
                <div className="flex items-start gap-2 font-medium text-gray-700 capitalize break-words">
                  <Building className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span>{selectedLog.employeeDepartment || 'N/A'}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block mb-1">Email</span>
                <div className="flex items-start gap-2 font-medium text-gray-700 break-all">
                  <Mail className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span>{selectedLog.employeeEmail || 'N/A'}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-400 block mb-1">Employee ID</span>
                <div className="flex items-start gap-2 font-medium text-gray-700 break-all">
                  <User className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span>{selectedLog.employeeShortId || selectedLog.employeeId || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {(() => {
            const statusInfo = getAttendanceStatusInfo(selectedLog.checkIn, selectedLog.checkOut, selectedLog.status);
            const statusColorInfo = {
              present: "bg-green-50 border-green-200 text-green-700",
              late: "bg-yellow-50 border-yellow-200 text-yellow-700",
              missed: "bg-orange-50 border-orange-200 text-orange-700",
              absent: "bg-red-50 border-red-200 text-red-700",
            }[statusInfo.status] || "bg-gray-50 border-gray-200 text-gray-700";

            return (
              <div className={`rounded-xl p-4 border ${statusColorInfo} flex flex-col md:flex-row items-start md:items-center justify-between shadow-sm gap-4`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full bg-white/60`}>
                    {getStatusIcon(statusInfo.status)}
                  </div>
                  <div>
                    <p className="text-xs font-semibold opacity-80 uppercase tracking-wider">Attendance Status</p>
                    <h4 className="text-lg font-bold capitalize">{statusInfo.label}</h4>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {statusInfo.status === 'late' && (
                    <span className="px-3 py-1 bg-white/60 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Late by {formatDuration(statusInfo.lateBy)}
                    </span>
                  )}
                  {(() => {
                    const earlyInfo = getEarlyOutInfo(selectedLog.checkOut);
                    if (earlyInfo?.isEarly) {
                      return (
                        <span className="px-3 py-1 bg-red-100 text-red-700 border border-red-200 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Early Left by {formatDuration(earlyInfo.earlyBy)}
                        </span>
                      )
                    }
                    return null;
                  })()}
                </div>
              </div>
            )
          })()}

          {/* Section 3: Time Logs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Check In</span>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {formatTime(selectedLog.checkIn)}
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
              <div className={`p-3 rounded-lg ${getEarlyOutInfo(selectedLog.checkOut)?.isEarly ? 'bg-red-50 text-red-600' : 'bg-purple-50 text-purple-600'}`}>
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Check Out</span>
                <div className={`text-xl font-bold mt-1 ${getEarlyOutInfo(selectedLog.checkOut)?.isEarly ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatTime(selectedLog.checkOut)}
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
              <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                <Timer className="h-6 w-6" />
              </div>
              <div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Working Hours</span>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {selectedLog.workingHours && selectedLog.workingHours !== 'N/A' && selectedLog.workingHours !== '0h 0m'
                    ? selectedLog.workingHours
                    : calculateWorkingHours(selectedLog.checkIn, selectedLog.checkOut)}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: All Punches Timeline */}
          {((selectedLog.checkInPunches && selectedLog.checkInPunches.length > 0) ||
            (selectedLog.checkOutPunches && selectedLog.checkOutPunches.length > 0)) && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                  Punch History
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Check-In Punches */}
                  {selectedLog.checkInPunches && selectedLog.checkInPunches.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                          <Clock className="h-4 w-4" />
                        </div>
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Check-In Times ({selectedLog.checkInPunches.length})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {selectedLog.checkInPunches.map((time, idx) => (
                          <div
                            key={`checkin-${idx}`}
                            className={`flex items-center gap-2 p-2 rounded-lg ${idx === 0
                              ? 'bg-blue-50 border border-blue-200'
                              : 'bg-gray-50 border border-gray-200'
                              }`}
                          >
                            <span className={`text-xs font-medium ${idx === 0 ? 'text-blue-600' : 'text-gray-500'
                              }`}>
                              #{idx + 1}
                            </span>
                            <span className={`text-sm font-bold ${idx === 0 ? 'text-blue-700' : 'text-gray-700'
                              }`}>
                              {formatTime(time)}
                            </span>
                            {idx === 0 && (
                              <span className="ml-auto text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                First
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Check-Out Punches */}
                  {selectedLog.checkOutPunches && selectedLog.checkOutPunches.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                          <Clock className="h-4 w-4" />
                        </div>
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Check-Out Times ({selectedLog.checkOutPunches.length})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {selectedLog.checkOutPunches.map((time, idx) => (
                          <div
                            key={`checkout-${idx}`}
                            className={`flex items-center gap-2 p-2 rounded-lg ${idx === selectedLog.checkOutPunches.length - 1
                              ? 'bg-purple-50 border border-purple-200'
                              : 'bg-gray-50 border border-gray-200'
                              }`}
                          >
                            <span className={`text-xs font-medium ${idx === selectedLog.checkOutPunches.length - 1 ? 'text-purple-600' : 'text-gray-500'
                              }`}>
                              #{idx + 1}
                            </span>
                            <span className={`text-sm font-bold ${idx === selectedLog.checkOutPunches.length - 1 ? 'text-purple-700' : 'text-gray-700'
                              }`}>
                              {formatTime(time)}
                            </span>
                            {idx === selectedLog.checkOutPunches.length - 1 && (
                              <span className="ml-auto text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                                Last
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Section 5: Notes (Row Wise) */}
          {(selectedLog.notes || selectedLog.remarks) && (
            <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-100">
              <h5 className="text-sm font-bold text-yellow-800 mb-2 flex items-center gap-2 uppercase tracking-wider">
                <FileText className="h-4 w-4" /> Notes / Remarks
              </h5>
              <p className="text-sm text-yellow-800/90 leading-relaxed bg-white/50 p-3 rounded-lg border border-yellow-100/50">
                {selectedLog.notes || selectedLog.remarks}
              </p>
            </div>
          )}

        </div>
      </div>
    );
  }

  const handleSelectAll = (e) => {
    const currentPageIds = attendanceLogs.map(log => log.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleDeleteLog = (log) => {
    setLogToDelete(log);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteLog = async () => {
    if (!logToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteLogMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} attendance log(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} attendance log(s) could not be deleted.`);
        }
      } else {
        await deleteLogMutation.mutateAsync(logToDelete.id);
        toast.success('Attendance log deleted successfully.');
      }
      setShowDeleteModal(false);
      setLogToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error('Failed to delete attendance log. Please try again.');
      }
    }
  };

  const handleCreateLog = async () => { };
  const handleEditLog = () => { };



  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Attendance Logs
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {selectedRows.size > 0 && (
              <PermissionGate permission="attendance" action="delete">
                <Button
                  variant="primary"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="attendance" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={
                    exportLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )
                  }
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={`flex items-center space-x-2 ${selectedRows.size > 0 ? "bg-blue-50 border-blue-200 text-blue-600" : ""}`}
                  aria-label={
                    selectedRows.size > 0
                      ? `Export ${selectedRows.size} selected attendance logs`
                      : "Export attendance logs"
                  }
                >
                  <span>
                    {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
                  </span>
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleAttendanceLogsSelectedFormatExport}
                />
              </div>
            </PermissionGate>

            <PermissionGate permission="attendance" action="create">
              <div className="relative">
                <Button
                  variant="primary"
                  icon={<Upload className="h-4 w-4" />}
                  onClick={() => setShowBulkUploadModal(true)}
                  className="bg-agrosaf-green hover:bg-agrosaf-green/90 text-white flex items-center gap-2"
                >
                  Upload Attendance
                </Button>
              </div>
            </PermissionGate>
          </div>
        </div>
      </div>
      {/* KPI Stats Cards */}
      <div className="p-6 pb-0 pt-0">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Card 1: Active Staff */}
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center space-x-4">
            <div className="p-3 bg-blue-50 rounded-lg shadow-inner">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Staff</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {isLoadingStats ? "..." : statsData?.totalEmployees || 0}
              </h3>
            </div>
          </div>

          {/* Card 2: Present */}
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center space-x-4">
            <div className="p-3 bg-green-50 rounded-lg shadow-inner">
              <UserCheck className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Present</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {isLoadingStats ? "..." : statsData?.presentToday || 0}
              </h3>
            </div>
          </div>

          {/* Card 3: Late Arrivals */}
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center space-x-4">
            <div className="p-3 bg-yellow-50 rounded-lg shadow-inner">
              <AlarmClock className="h-8 w-8 text-yellow-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Late Arrivals</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {isLoadingStats ? "..." : statsData?.lateToday || 0}
              </h3>
            </div>
          </div>

          {/* Card 4: Absent */}
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center space-x-4">
            <div className="p-3 bg-red-50 rounded-lg shadow-inner">
              <UserX className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Absent</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {isLoadingStats ? "..." : statsData?.absentToday || 0}
              </h3>
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by name, ID, email..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  // Maintain focus on search input
                  const searchInput = document.querySelector('input[placeholder="Search by name, ID, email..."]');
                  if (searchInput) {
                    setTimeout(() => searchInput.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="missed">Missed</option>
              <option value="absent">Absent</option>
            </select>
          </div>

          {/* Date Filter */}
          {/* Date Filter Range */}
          <div className="flex items-center gap-2">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={(range) => {
                setFilterStartDate(range.startDate);
                setFilterEndDate(range.endDate);
              }}
            />

            {/* Clear Filters */}
            {(searchTerm || filterStatus !== "all" || filterStartDate || filterEndDate) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("all");
                  setFilterStartDate("");
                  setFilterEndDate("");
                }}
                className="h-[44px] border-gray-200 text-gray-600 hover:bg-gray-50 transition-all rounded-lg shadow-sm px-4 py-0 text-sm font-semibold"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Attendance Logs Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">
              Loading attendance logs...
            </span>
          </div>
        ) : (
          <table className="w-full mt-4 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={attendanceLogs.length > 0 && selectedRows.size === attendanceLogs.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                  />
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('employee')}
                >
                  <div className="flex items-center">
                    Employee {getSortIcon('employee')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('date')}
                >
                  <div className="flex items-center">
                    Date {getSortIcon('date')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('checkIn')}
                >
                  <div className="flex items-center">
                    Check In {getSortIcon('checkIn')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('checkOut')}
                >
                  <div className="flex items-center">
                    Check Out {getSortIcon('checkOut')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('workingHours')}
                >
                  <div className="flex items-center">
                    Working Hours {getSortIcon('workingHours')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('status')}
                >
                  <div className="flex items-center">
                    Status {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {attendanceLogs.map((log) => {
                const statusInfo = getAttendanceStatusInfo(log.checkIn, log.checkOut, log.status);
                const earlyInfo = getEarlyOutInfo(log.checkOut);
                const isEarly = earlyInfo?.isEarly;

                return (
                  <tr key={log.id} className={`hover:bg-gray-50 ${selectedRows.has(log.id) ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(log.id)}
                        onChange={() => handleSelectRow(log.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                      />
                    </td>
                    <td className="px-3 py-4 overflow-hidden max-w-xs">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate capitalize" title={log.employeeName || "Unknown Employee"}>
                          {log.employeeName || "Unknown Employee"}
                        </div>
                        <div className="text-sm text-gray-500 truncate" title={log.employeeEmail || "N/A"}>
                          {log.employeeEmail || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500 truncate" title={`Employee ID: ${log.employeeId || "N/A"}`}>
                          ID: {log.employeeId || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.date ? formatDate(log.date, { includeTime: false }) : "N/A"}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                          <span>{formatTime(log.checkIn)}</span>
                          {log.checkInPunches && log.checkInPunches.length > 1 && (
                            <span
                              className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full cursor-help"
                              title={`All Check-Ins:\n${log.checkInPunches.map((time, idx) => `${idx + 1}. ${time}`).join('\n')}`}
                            >
                              {log.checkInPunches.length}
                            </span>
                          )}
                        </div>
                        {log.checkinLate && log.checkinLateTime && log.checkinLateTime !== '00:00:00' && (
                          <span className="text-[10px] text-red-500 font-medium mt-0.5" title="Late Check-in Time">
                            Late: {log.checkinLateTime}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                          <span>{formatTime(log.checkOut)}</span>
                          {log.checkOutPunches && log.checkOutPunches.length > 1 && (
                            <span
                              className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-purple-600 bg-purple-100 rounded-full cursor-help"
                              title={`All Check-Outs:\n${log.checkOutPunches.map((time, idx) => `${idx + 1}. ${time}`).join('\n')}`}
                            >
                              {log.checkOutPunches.length}
                            </span>
                          )}
                        </div>
                        {log.checkoutEarly && log.checkoutEarlyTime && log.checkoutEarlyTime !== '00:00:00' && (
                          <span className="text-[10px] text-red-500 font-medium mt-0.5" title="Early Left Time">
                            Early: {log.checkoutEarlyTime}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.workingHours && log.workingHours !== 'N/A' && log.workingHours !== '0h 0m'
                        ? log.workingHours
                        : calculateWorkingHours(log.checkIn, log.checkOut)}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                            statusInfo.status
                          )}`}
                          title={statusInfo.lateBy ? `Late by ${formatDuration(statusInfo.lateBy)}` : ''}
                        >
                          {statusInfo.status
                            .replace(/_/g, ' ')
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="attendance" action="read">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(log)}
                            className="text-blue-600 hover:text-blue-900"
                            aria-label="View attendance log details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="attendance" action="delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLog(log)}
                            className="text-red-500 hover:text-red-800"
                            aria-label="Delete attendance log"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </td>


                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>




      {
        !isLoading && attendanceLogs.length === 0 && (
          <div className="text-center py-12">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No attendance logs found
            </h3>
          </div>
        )
      }

      {/* Pagination */}
      {/* Pagination */}
      <PaginationControls
        currentPage={pagination?.currentPage || 1}
        totalPages={pagination?.totalPages || 1}
        totalItems={Number(pagination?.totalLogs || attendanceLogs.length || 0)}
        limit={itemsPerPage || 10}
        hasNextPage={pagination?.hasNextPage}
        hasPrevPage={pagination?.hasPrevPage}
        onPageChange={(page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onLimitChange={(newLimit) => {
          handleItemsPerPageChange(newLimit);
          setCurrentPage(1);
        }}
        limitOptions={[10, 20, 50, 100]}
      />

      {/* Create Modal */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">
                Create Attendance Log
              </h3>
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee
                  </label>
                  <div className="relative employee-dropdown-container">
                    {/* Display selected employee or placeholder */}
                    <div
                      onClick={() => {
                        setEmployeeSearchOpen(!employeeSearchOpen);
                        if (!employeeSearchOpen) {
                          setEmployeeSearchTerm("");
                        }
                        // Don't clear selectedEmployeeData when toggling dropdown
                      }}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 min-w-0"
                    >
                      <span className={`${formData.employee ? "text-gray-900" : "text-gray-500"} truncate flex-1 min-w-0 mr-2`} title={formData.employee && selectedEmployeeData ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""} (${selectedEmployeeData.employeeId || ""})` : formData.employee ? `Employee ID: ${formData.employee}` : "Select Employee"}>
                        {formData.employee && selectedEmployeeData
                          ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                          } (${selectedEmployeeData.employeeId || ""})`
                          : formData.employee
                            ? `Employee ID: ${formData.employee}`
                            : "Select Employee"}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${employeeSearchOpen ? "transform rotate-180" : ""
                          }`}
                      />
                    </div>

                    {/* Dropdown with search */}
                    {employeeSearchOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by employee name..."
                              value={employeeSearchTerm}
                              onChange={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                setEmployeeSearchTerm(value);
                              }}
                              onKeyPress={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={100}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {isLoadingEmployeeSearch ? (
                            <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                              <span>Loading employees...</span>
                            </div>
                          ) : dropdownEmployees.length > 0 ? (
                            dropdownEmployees.map((employee) => (
                              <div
                                key={employee._id}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    employee: employee._id,
                                  });
                                  // Store the selected employee data for display
                                  setSelectedEmployeeData(employee);
                                  setEmployeeSearchOpen(false);
                                  setEmployeeSearchTerm("");
                                }}
                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.employee === employee._id
                                  ? "bg-blue-50"
                                  : ""
                                  }`}
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.personalInfo?.firstName || ""}{" "}
                                  {employee.personalInfo?.lastName || ""}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {employee.employeeId || ""} -{" "}
                                  {employee.employmentInfo?.department || ""}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedEmployeeSearch.trim()
                                ? "No employees found"
                                : "Type to search employees..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isLoadingEmployeeSearch
                      ? "Fetching employees..."
                      : dropdownEmployees.length > 0
                        ? `Found ${dropdownEmployees.length} employee(s)`
                        : "Type to search employees..."}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock In Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockIn}
                      onChange={(e) =>
                        setFormData({ ...formData, clockIn: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock Out Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockOut}
                      onChange={(e) =>
                        setFormData({ ...formData, clockOut: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                      setFormData({ ...formData, remarks: value });
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={500}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.remarks.length}/500 characters
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedEmployeeData(null);
                    setEmployeeSearchOpen(false);
                    setEmployeeSearchTerm("");
                  }}
                >
                  Cancel
                </Button>
                <PermissionGate permission="attendance" action="create">
                  <Button variant="primary" onClick={handleCreateLog}>
                    Create
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Modal */}
      {
        showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Edit Attendance Log</h3>
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee
                  </label>
                  <div className="relative employee-dropdown-container">
                    {/* Display selected employee or placeholder */}
                    <div
                      onClick={() => {
                        setEmployeeSearchOpen(!employeeSearchOpen);
                        if (!employeeSearchOpen) {
                          setEmployeeSearchTerm("");
                        }
                        // Don't clear selectedEmployeeData when toggling dropdown
                      }}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 min-w-0"
                    >
                      <span className={`${formData.employee ? "text-gray-900" : "text-gray-500"} truncate flex-1 min-w-0 mr-2`} title={formData.employee && selectedEmployeeData ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""} (${selectedEmployeeData.employeeId || ""})` : formData.employee ? `Employee ID: ${formData.employee}` : "Select Employee"}>
                        {formData.employee && selectedEmployeeData
                          ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                          } (${selectedEmployeeData.employeeId || ""})`
                          : formData.employee
                            ? `Employee ID: ${formData.employee}`
                            : "Select Employee"}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${employeeSearchOpen ? "transform rotate-180" : ""
                          }`}
                      />
                    </div>

                    {/* Dropdown with search */}
                    {employeeSearchOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by employee name..."
                              value={employeeSearchTerm}
                              onChange={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                setEmployeeSearchTerm(value);
                              }}
                              onKeyPress={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={100}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {isLoadingEmployeeSearch ? (
                            <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                              <span>Loading employees...</span>
                            </div>
                          ) : dropdownEmployees.length > 0 ? (
                            dropdownEmployees.map((employee) => (
                              <div
                                key={employee._id}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    employee: employee._id,
                                  });
                                  // Store the selected employee data for display
                                  setSelectedEmployeeData(employee);
                                  setEmployeeSearchOpen(false);
                                  setEmployeeSearchTerm("");
                                }}
                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.employee === employee._id
                                  ? "bg-blue-50"
                                  : ""
                                  }`}
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.personalInfo?.firstName || ""}{" "}
                                  {employee.personalInfo?.lastName || ""}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {employee.employeeId || ""} -{" "}
                                  {employee.employmentInfo?.department || ""}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedEmployeeSearch.trim()
                                ? "No employees found"
                                : "Type to search employees..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isLoadingEmployeeSearch
                      ? "Fetching employees..."
                      : dropdownEmployees.length > 0
                        ? `Found ${dropdownEmployees.length} employee(s)`
                        : "Type to search employees..."}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock In Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockIn}
                      onChange={(e) =>
                        setFormData({ ...formData, clockIn: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Clock Out Time
                    </label>
                    <input
                      type="time"
                      value={formData.clockOut}
                      onChange={(e) =>
                        setFormData({ ...formData, clockOut: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="HH:MM"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                      setFormData({ ...formData, remarks: value });
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={500}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.remarks.length}/500 characters
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEmployeeData(null);
                    setEmployeeSearchOpen(false);
                    setEmployeeSearchTerm("");
                  }}
                >
                  Cancel
                </Button>
                <PermissionGate permission="attendance" action="update">
                  <Button variant="primary" onClick={handleEditLog}>
                    Update
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }


      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Attendance Logs"
        showDateRange
        scopedFormatExport={{
          onExport: handleAttendanceLogsScopedExport,
          formats: ["csv", "xlsx", "pdf"],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setLogToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteLog}
        title={isBulkDelete ? "Delete Attendance Logs" : "Delete Attendance Log"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected attendance logs? This action cannot be undone.`
            : "Are you sure you want to delete this attendance log? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          // Refetch attendance logs after successful upload
          queryClient.invalidateQueries(["biometricAttendance", currentPage, pageSize, searchTerm, filterStatus, filterStartDate, filterEndDate]);
          setShowBulkUploadModal(false);
          toast.success("Attendance logs uploaded successfully");
        }}
        config={{
          title: 'Bulk Upload Attendance',
          subtitle: 'Import biometric or manual attendance logs using an Excel file',
          templateEndpoint: '/biometric-attendance/bulk-upload/template',
          validateEndpoint: '/biometric-attendance/bulk-upload/validate',
          uploadEndpoint: '/biometric-attendance/bulk-upload',
          templateFilename: 'attendance_bulk_upload_template.xlsx',
          accept: '.xlsx,.xls',
          allowedFormats: ['excel'],
          dataKey: 'data',
          instructions: {
            download: [
              'Download the attendance Excel template',
              'Fill in the record details',
              'Required: Employee Code, Card No, In Time, Out Time'
            ],
            prepare: [
              'Use date format: DD/MM/YYYY HH:mm:ss',
              'Total Minute, Shift and Remarks are optional',
              'New employees will be automatically created if not found'
            ]
          }
        }}
      />
    </div>
  );
};

export default AttendanceLogs;
